import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Run {
  _id: string;
  username: string;
  action: string;
  timestamp: string;
}

export function InstagramRuns() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<Run[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  async function fetchRuns() {
    if (!id) return;
    
    try {
      const response = await fetch(`/api/automations/instagram/${id}/runs`);
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
      } else {
        console.error("Failed to fetch runs");
      }
    } catch (error) {
      console.error("Failed to fetch runs:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchAutomation() {
    if (!id) return;
    
    try {
      const response = await fetch(`/api/automations/instagram/${id}`);
      if (response.ok) {
        const data = await response.json();
        const automation = data.automation;
        const keywordLabel = automation?.keyword || "Untitled";
        setBreadcrumbs([
          { label: "Flows", path: "/automations" },
          { label: "Instagram", path: "/automations/instagram" },
          { label: keywordLabel, path: `/automations/instagram/${id}/edit` },
          { label: "Runs", path: `/automations/instagram/${id}/runs` },
        ]);
      } else {
        console.error("Failed to fetch automation");
        setBreadcrumbs([
          { label: "Flows", path: "/automations" },
          { label: "Instagram", path: "/automations/instagram" },
          { label: "Runs", path: `/automations/instagram/${id}/runs` },
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch automation:", error);
      setBreadcrumbs([
        { label: "Flows", path: "/automations" },
        { label: "Instagram", path: "/automations/instagram" },
        { label: "Runs", path: `/automations/instagram/${id}/runs` },
      ]);
    }
  }

  useEffect(() => {
    fetchRuns();
    fetchAutomation();

    // Cleanup: clear breadcrumbs when component unmounts
    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  function formatDate(dateString?: string) {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return "-";
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-4">
      <Tabs value="runs" className="mb-4">
        <TabsList>
          <TabsTrigger
            value="edit"
            onClick={() => navigate(`/automations/instagram/${id}/edit`)}
          >
            Edit
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            onClick={() => navigate(`/automations/instagram/${id}/runs`)}
          >
            Runs
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Flow Runs</h1>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Username</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Timestamp</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs && runs.length > 0 ? (
            runs.map((run) => (
              <TableRow key={run._id}>
                <TableCell>
                  {run.username ? (
                    <a
                      href={`https://instagram.com/${run.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {run.username}
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <span className="capitalize">{run.action || "-"}</span>
                </TableCell>
                <TableCell>{formatDate(run.timestamp)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="h-24 text-center">
                No runs found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

